import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';

const TeamPerformance = ({ teams, matches }) => {
  const [teamStats, setTeamStats] = useState([]);
  
  useEffect(() => {
    // Calculate team performance statistics
    const stats = teams.map(team => {
      // Get matches for this team
      const teamMatches = matches.filter(match => 
        match.homeTeam.id === team.id || match.awayTeam.id === team.id
      );
      
      if (teamMatches.length === 0) {
        return {
          id: team.id,
          name: team.name,
          color: team.color,
          played: 0,
          won: 0,
          drawn: 0,
          lost: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          goalDifference: 0,
          winRate: 0,
          avgGoalsFor: 0,
          avgGoalsAgainst: 0
        };
      }
      
      // Calculate stats
      let won = 0;
      let drawn = 0;
      let lost = 0;
      let goalsFor = 0;
      let goalsAgainst = 0;
      
      teamMatches.forEach(match => {
        const isHome = match.homeTeam.id === team.id;
        const teamScore = isHome ? match.homeScore : match.awayScore;
        const opponentScore = isHome ? match.awayScore : match.homeScore;
        
        goalsFor += teamScore;
        goalsAgainst += opponentScore;
        
        if (teamScore > opponentScore) {
          won += 1;
        } else if (teamScore < opponentScore) {
          lost += 1;
        } else {
          drawn += 1;
        }
      });
      
      const played = teamMatches.length;
      const goalDifference = goalsFor - goalsAgainst;
      const winRate = (won / played) * 100;
      const avgGoalsFor = goalsFor / played;
      const avgGoalsAgainst = goalsAgainst / played;
      
      return {
        id: team.id,
        name: team.name,
        color: team.color,
        played,
        won,
        drawn,
        lost,
        goalsFor,
        goalsAgainst,
        goalDifference,
        winRate,
        avgGoalsFor,
        avgGoalsAgainst
      };
    });
    
    // Sort by win rate
    setTeamStats(stats.sort((a, b) => b.winRate - a.winRate));
  }, [teams, matches]);
  
  return (
    <div className="space-y-4">
      <Card>
        <h2 className="text-lg font-semibold mb-3">Team Performance</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b dark:border-gray-700">
                <th className="text-left py-2 px-2">Team</th>
                <th className="text-center py-2 px-2">P</th>
                <th className="text-center py-2 px-2">W</th>
                <th className="text-center py-2 px-2">D</th>
                <th className="text-center py-2 px-2">L</th>
                <th className="text-center py-2 px-2">Win %</th>
              </tr>
            </thead>
            <tbody>
              {teamStats.map(team => (
                <tr key={team.id} className="border-b dark:border-gray-700 last:border-0">
                  <td className="py-3 px-2">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: team.color }}
                      />
                      <span className="font-medium">{team.name}</span>
                    </div>
                  </td>
                  <td className="text-center py-3 px-2">{team.played}</td>
                  <td className="text-center py-3 px-2">{team.won}</td>
                  <td className="text-center py-3 px-2">{team.drawn}</td>
                  <td className="text-center py-3 px-2">{team.lost}</td>
                  <td className="text-center py-3 px-2 font-medium">
                    {team.played > 0 ? `${team.winRate.toFixed(1)}%` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      
      <Card>
        <h2 className="text-lg font-semibold mb-3">Goal Statistics</h2>
        
        <div className="grid grid-cols-2 gap-4">
          {/* Goals Scored Chart */}
          <div>
            <h3 className="text-sm font-medium mb-2">Goals Scored</h3>
            <div className="space-y-2">
              {teamStats
                .sort((a, b) => b.goalsFor - a.goalsFor)
                .map(team => {
                  const maxGoals = Math.max(...teamStats.map(t => t.goalsFor));
                  const width = team.goalsFor > 0 ? (team.goalsFor / maxGoals) * 100 : 0;
                  
                  return (
                    <div key={`scored-${team.id}`} className="flex items-center">
                      <div className="w-24 truncate mr-2 text-xs">
                        {team.name}
                      </div>
                      <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full flex items-center pl-2 text-white text-xs font-medium"
                          style={{ 
                            width: `${width}%`, 
                            backgroundColor: team.color,
                            minWidth: '24px'
                          }}
                        >
                          {team.goalsFor}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          
          {/* Goals Conceded Chart */}
          <div>
            <h3 className="text-sm font-medium mb-2">Goals Conceded</h3>
            <div className="space-y-2">
              {teamStats
                .sort((a, b) => a.goalsAgainst - b.goalsAgainst)
                .map(team => {
                  const maxGoals = Math.max(...teamStats.map(t => t.goalsAgainst));
                  const width = team.goalsAgainst > 0 ? (team.goalsAgainst / maxGoals) * 100 : 0;
                  
                  return (
                    <div key={`conceded-${team.id}`} className="flex items-center">
                      <div className="w-24 truncate mr-2 text-xs">
                        {team.name}
                      </div>
                      <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full flex items-center pl-2 text-white text-xs font-medium"
                          style={{ 
                            width: `${width}%`,
                            backgroundColor: 'rgba(100, 100, 100, 0.6)',
                            minWidth: '24px' 
                          }}
                        >
                          {team.goalsAgainst}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TeamPerformance;
